import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import { AuthContext } from './auth-context';
import type { User } from './auth-context';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const userData = response.data.user;

      // Check localStorage for onboarding status
      const hasSeenOnboarding = localStorage.getItem(`onboarding_${userData.id}`) === 'true';

      // Use systemPrompt from backend (userData.systemPrompt comes from server)
      const systemPrompt = userData.systemPrompt || '';

      setUser({ ...userData, hasSeenOnboarding, systemPrompt });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const userData = response.data.user;

    // Check localStorage for onboarding status
    const hasSeenOnboarding = localStorage.getItem(`onboarding_${userData.id}`) === 'true';

    // Use systemPrompt from backend
    const systemPrompt = userData.systemPrompt || '';

    setUser({ ...userData, hasSeenOnboarding, systemPrompt });
  };

  const register = async (email: string, password: string) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password
    });
    const userData = response.data.user;

    // New users haven't seen onboarding
    setUser({ ...userData, hasSeenOnboarding: false });
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`);
    setUser(null);
  };

  const markOnboardingComplete = () => {
    if (user) {
      localStorage.setItem(`onboarding_${user.id}`, 'true');
      setUser({ ...user, hasSeenOnboarding: true });
    }
  };

  const updateSystemPrompt = async (prompt: string) => {
    if (user) {
      try {
        // Update on backend
        await axios.put(`${API_URL}/api/users/system-prompt`, {
          systemPrompt: prompt
        });

        // Update local state
        setUser({ ...user, systemPrompt: prompt });
      } catch (error) {
        console.error('Failed to update system prompt:', error);
        throw error;
      }
    }
  };

  const updateMaxTimeout = async (timeoutMs: number) => {
    if (user) {
      try {
        // Update on backend
        await axios.put(`${API_URL}/api/users/timeout`, {
          maxClaudeCodeTimeout: timeoutMs
        });

        // Update local state
        setUser({ ...user, maxClaudeCodeTimeout: timeoutMs });
      } catch (error) {
        console.error('Failed to update max timeout:', error);
        throw error;
      }
    }
  };

  const updateModelMode = async (mode: 'standard' | 'budget' | 'test' | null) => {
    if (user) {
      try {
        // Update on backend
        await axios.put(`${API_URL}/api/users/model-mode`, {
          modelMode: mode
        });

        // Update local state
        setUser({ ...user, modelMode: mode });
      } catch (error) {
        console.error('Failed to update model mode:', error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, markOnboardingComplete, updateSystemPrompt, updateMaxTimeout, updateModelMode }}>
      {children}
    </AuthContext.Provider>
  );
}
